/**
 * BottomNavigation component
 * Mobile-first bottom navigation bar with main app sections
 * Only visible on mobile/tablet devices (hidden on desktop)
 */

import { Link, useMatchRoute, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { RootSearchParams } from '@/routes/__root';

/**
 * BottomNavigation component
 *
 * Features:
 * - Fixed bottom navigation for mobile devices
 * - Active state highlighting based on current route
 * - Touch-friendly 48px height buttons
 * - Icons with labels
 * - Hidden on desktop (lg breakpoint and above)
 *
 * @example
 * ```tsx
 * <BottomNavigation />
 * ```
 */
export function BottomNavigation() {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();

  // Get root search params to preserve week when navigating (exclude page param)
  const rootSearch = useSearch({ from: '__root__' }) as RootSearchParams;
  const { week, redirect } = rootSearch;
  const navSearch = { week, redirect } as RootSearchParams;

  // Helper to check if route is active
  const isRouteActive = (to: string) => {
    if (to === '/') {
      // For home, only match exact path
      return matchRoute({ to: '/', fuzzy: false }) !== false;
    }
    // For other routes, use fuzzy matching
    return matchRoute({ to, fuzzy: true }) !== false;
  };

  // Define navigation items
  const navItems = [
    {
      to: '/',
      label: t('navigation.home'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
      show: true,
    },
    {
      to: '/availability',
      label: t('navigation.availability'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      show: true,
    },
  ];

  // Filter items based on visibility
  const visibleItems = navItems.filter((item) => item.show);

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40"
      role="navigation"
      aria-label={t('navigation.mainNavigation')}
    >
      <div className="flex justify-around items-center h-16">
        {visibleItems.map((item) => {
          const isActive = isRouteActive(item.to);

          return (
            <Link
              key={item.to}
              to={item.to}
              search={navSearch}
              className={`flex flex-col items-center justify-center flex-1 h-full min-w-0 transition-colors ${
                isActive
                  ? 'text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className="flex flex-col items-center justify-center gap-1">
                {item.icon}
                <span className="text-xs font-medium truncate max-w-full px-1">
                  {item.label}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
